import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { SEOHead } from '@/components/seo';
import { Header, Footer, FeesSection, FilingsSection } from '@/components/public';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, 
  Check, 
  ChevronRight,
  ChevronDown,
  Building2,
  DollarSign,
  Shield,
  Clock,
  FileText,
  Scale,
  Globe,
  Users,
  MapPin
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_NAME = 'StartEase';
const BASE_URL = 'https://starteaseai.com';
const APP_URL = '/login';

// Comprehensive state-specific content
const STATE_CONTENT = {
  'wyoming': {
    tagline: 'The Most Business-Friendly State in America',
    overview: `Wyoming has consistently ranked as the #1 state for business formation due to its extremely favorable tax environment, strong privacy protections, and minimal reporting requirements. With no state income tax, no franchise tax, and the lowest LLC filing fees in the country, Wyoming is the premier destination for entrepreneurs worldwide.`,
    
    whyChoose: {
      title: 'Why Form Your Business in Wyoming?',
      subtitle: 'Wyoming offers unmatched advantages for business owners',
      benefits: [
        {
          icon: 'DollarSign',
          title: 'No State Income Tax',
          description: 'Wyoming has no personal or corporate income tax. Your business profits are not taxed at the state level, maximizing your earnings.'
        },
        {
          icon: 'Shield',
          title: 'Strong Privacy Laws',
          description: 'Wyoming offers nominee services and does not require disclosure of LLC members/managers in public records, protecting owner identities.'
        },
        {
          icon: 'FileText',
          title: 'Low Filing Fees',
          description: 'At just $100 for LLC formation and $100/year for annual reports, Wyoming has among the lowest fees in the nation.'
        },
        {
          icon: 'Scale',
          title: 'Asset Protection',
          description: 'Wyoming pioneered charging order protection, meaning creditors cannot seize LLC interests for personal debts of members.'
        },
        {
          icon: 'Clock',
          title: 'Fast Processing',
          description: 'Wyoming Secretary of State processes filings quickly, typically within 1-3 business days for standard processing.'
        },
        {
          icon: 'Globe',
          title: 'No Residency Required',
          description: 'Anyone worldwide can form a Wyoming entity. No US citizenship, residency, or presence required.'
        }
      ]
    },
    
    taxInfo: {
      title: 'Wyoming Tax Environment',
      items: [
        { label: 'State Income Tax', value: 'None', highlight: true },
        { label: 'Corporate Income Tax', value: 'None', highlight: true },
        { label: 'Franchise Tax', value: 'None', highlight: true },
        { label: 'Sales Tax', value: '4%', highlight: false },
        { label: 'Property Tax', value: 'Low (0.61% avg)', highlight: false },
        { label: 'Estate/Inheritance Tax', value: 'None', highlight: true }
      ]
    },
    
    complianceRequirements: {
      title: 'Wyoming Annual Compliance',
      items: [
        {
          requirement: 'Annual Report',
          deadline: 'Anniversary of formation',
          fee: '$60 (LLC) / $50 (Corp)',
          notes: 'Simple one-page report filed online'
        },
        {
          requirement: 'Registered Agent',
          deadline: 'Continuous',
          fee: 'Included in our service',
          notes: 'Must maintain Wyoming address'
        },
        {
          requirement: 'Business License',
          deadline: 'Varies by business type',
          fee: 'Varies',
          notes: 'Some businesses require state licenses'
        }
      ]
    },
    
    faqs: [
      {
        question: 'Can I form a Wyoming LLC if I don\'t live in Wyoming?',
        answer: 'Yes! Wyoming has no residency requirements. You can form and operate a Wyoming LLC from anywhere in the world. You simply need a registered agent with a Wyoming address (which we provide).'
      },
      {
        question: 'Is Wyoming good for e-commerce businesses?',
        answer: 'Yes, Wyoming is excellent for e-commerce. With no state income tax and strong privacy protections, many online sellers form Wyoming LLCs. However, you may need to collect sales tax for sales into other states under economic nexus laws.'
      },
      {
        question: 'What is Wyoming\'s charging order protection?',
        answer: 'Wyoming provides "exclusive" charging order protection, meaning a creditor with a judgment against an LLC member personally can only receive a charging order (right to distributions). They cannot force liquidation or take control of the LLC interest.'
      },
      {
        question: 'How fast can I form a Wyoming LLC?',
        answer: 'Wyoming processes filings within 1-3 business days for standard processing. Expedited processing (24-hour and same-day) is available for an additional fee.'
      },
      {
        question: 'Do I need a Wyoming bank account?',
        answer: 'No. You can open a business bank account at any US bank with your Wyoming LLC. Many banks offer online account opening for Wyoming entities.'
      },
      {
        question: 'What\'s the difference between Wyoming and Delaware?',
        answer: 'Both states are business-friendly. Wyoming offers lower fees ($100 vs $90+$300 franchise tax), no income tax, and stronger privacy. Delaware is preferred for VC-backed startups due to its established corporate courts. For most small businesses, Wyoming is the better choice.'
      }
    ]
  },
  
  'delaware': {
    tagline: 'The Corporate Capital of America',
    overview: `Delaware is home to more than 1.5 million registered businesses, including over 66% of Fortune 500 companies. The state's sophisticated corporate laws, specialized Court of Chancery, and business-friendly environment make it the default choice for corporations planning to raise venture capital or go public.`,
    
    whyChoose: {
      title: 'Why Form Your Business in Delaware?',
      subtitle: 'Delaware sets the standard for corporate law',
      benefits: [
        {
          icon: 'Scale',
          title: 'Court of Chancery',
          description: 'Specialized business court with experienced judges (no juries). Provides predictable, efficient resolution of corporate disputes.'
        },
        {
          icon: 'DollarSign',
          title: 'VC Standard',
          description: 'Venture capitalists universally accept Delaware C-Corps. Standard legal documents reduce negotiation time and legal costs.'
        },
        {
          icon: 'FileText',
          title: 'Developed Corporate Law',
          description: 'Decades of case law provide clarity on corporate governance issues. Lawyers and investors understand Delaware structures.'
        },
        {
          icon: 'Shield',
          title: 'No State Tax on Out-of-State Income',
          description: 'Delaware does not tax income earned outside the state. Companies operating elsewhere pay no Delaware corporate income tax.'
        },
        {
          icon: 'Building2',
          title: 'Business-Friendly Laws',
          description: 'Delaware General Corporation Law is updated annually to reflect modern business needs. Directors have broad protections.'
        },
        {
          icon: 'Globe',
          title: 'International Recognition',
          description: 'Delaware corporations are recognized and respected worldwide. Ideal for companies with international operations or investors.'
        }
      ]
    },
    
    taxInfo: {
      title: 'Delaware Tax Environment',
      items: [
        { label: 'Franchise Tax (Corp)', value: '$175-$200K+/year', highlight: false },
        { label: 'State Income Tax (Individuals)', value: '2.2%-6.6%', highlight: false },
        { label: 'Corporate Income Tax', value: '8.7% (on DE income)', highlight: false },
        { label: 'No Tax on Out-of-State Income', value: 'Yes', highlight: true },
        { label: 'Sales Tax', value: 'None', highlight: true },
        { label: 'LLC Annual Tax', value: '$300/year', highlight: false }
      ]
    },
    
    complianceRequirements: {
      title: 'Delaware Annual Compliance',
      items: [
        {
          requirement: 'Franchise Tax Report (Corp)',
          deadline: 'March 1',
          fee: '$175 minimum',
          notes: 'Calculated by authorized shares or assumed par value method'
        },
        {
          requirement: 'Annual Report (Corp)',
          deadline: 'March 1',
          fee: 'Included with franchise tax',
          notes: 'Filed with Division of Corporations'
        },
        {
          requirement: 'Annual Tax (LLC)',
          deadline: 'June 1',
          fee: '$300',
          notes: 'Flat fee regardless of income'
        },
        {
          requirement: 'Registered Agent',
          deadline: 'Continuous',
          fee: 'Included in our service',
          notes: 'Required Delaware address'
        }
      ]
    },
    
    faqs: [
      {
        question: 'Why do startups choose Delaware?',
        answer: 'VCs require Delaware C-Corps because: 1) Standard legal documents reduce costs, 2) Court of Chancery provides predictable dispute resolution, 3) Delaware corporate law is well-established, 4) QSBS benefits are better understood with Delaware corps.'
      },
      {
        question: 'What is the Delaware Franchise Tax?',
        answer: 'Delaware charges corporations an annual franchise tax. Most startups use the Authorized Shares Method: $175 for 5,000 shares or less, ~$400 for 10M shares. Due March 1 annually. Late fees are steep ($200+).'
      },
      {
        question: 'Should my LLC be formed in Delaware?',
        answer: 'For LLCs, Delaware offers fewer advantages than Wyoming. Delaware charges $300/year LLC tax plus $90 filing fee. Wyoming charges $100 filing and $60/year with no additional taxes. Choose Delaware LLC only if you specifically need Delaware courts.'
      },
      {
        question: 'Do I need to have an office in Delaware?',
        answer: 'No. You only need a registered agent in Delaware (which we provide). Most Delaware companies operate entirely outside the state.'
      },
      {
        question: 'What is the Court of Chancery?',
        answer: 'Delaware\'s Court of Chancery is a specialized business court dating to 1792. It handles corporate disputes without juries, providing faster, more predictable outcomes. Judges are experts in corporate law.'
      }
    ]
  },
  
  'texas': {
    tagline: 'The Lone Star State for Business Growth',
    overview: `Texas boasts the second-largest economy in the United States and has become a magnet for businesses relocating from high-tax states. With no personal income tax, a massive consumer market, and business-friendly regulations, Texas offers tremendous opportunities for growth.`,
    
    whyChoose: {
      title: 'Why Form Your Business in Texas?',
      subtitle: 'Texas offers scale and opportunity',
      benefits: [
        {
          icon: 'DollarSign',
          title: 'No Personal Income Tax',
          description: 'Texas has no state personal income tax. Business owners keep more of what they earn.'
        },
        {
          icon: 'Users',
          title: 'Massive Market',
          description: '29+ million residents make Texas the second-largest consumer market in the US.'
        },
        {
          icon: 'Building2',
          title: 'Business Relocations',
          description: 'Major companies (Tesla, Oracle, HP) have relocated to Texas, creating a thriving business ecosystem.'
        },
        {
          icon: 'Globe',
          title: 'International Trade Hub',
          description: 'Ports, airports, and proximity to Mexico make Texas ideal for international commerce.'
        },
        {
          icon: 'FileText',
          title: 'Business-Friendly Regulations',
          description: 'Texas consistently ranks among the most business-friendly states for regulations and permits.'
        },
        {
          icon: 'Shield',
          title: 'Strong Legal Framework',
          description: 'Texas Business Organizations Code provides clear structure for LLCs and corporations.'
        }
      ]
    },
    
    taxInfo: {
      title: 'Texas Tax Environment',
      items: [
        { label: 'Personal Income Tax', value: 'None', highlight: true },
        { label: 'Franchise Tax (Margin Tax)', value: '0.375%-0.75%', highlight: false },
        { label: 'No Tax Threshold', value: '$1.23M revenue', highlight: true },
        { label: 'Sales Tax', value: '6.25% + local', highlight: false },
        { label: 'Property Tax', value: 'Higher (1.8% avg)', highlight: false }
      ]
    },
    
    faqs: [
      {
        question: 'What is the Texas Franchise Tax?',
        answer: 'Texas charges a franchise tax (margin tax) on businesses with revenue over $1.23 million. Rates are 0.375% for retail/wholesale and 0.75% for other businesses. Businesses under the threshold file a "No Tax Due" report.'
      },
      {
        question: 'Is Texas good for small businesses?',
        answer: 'Yes! With no personal income tax and franchise tax exemption for businesses under $1.23M revenue, small businesses thrive in Texas. The large population also provides a substantial customer base.'
      },
      {
        question: 'How does Texas compare to Wyoming for LLC formation?',
        answer: 'Texas has higher filing fees ($300 vs $100) and the franchise tax for larger businesses. Wyoming is better for privacy and simplicity. Texas is better if you physically operate in Texas or want access to the Texas market.'
      }
    ]
  },
  
  'florida': {
    tagline: 'The Gateway to the Americas',
    overview: `Florida offers no personal income tax, access to Latin American markets, and a growing population of entrepreneurs and professionals. With year-round tourism and a diverse economy, Florida is ideal for businesses targeting both domestic and international markets.`,
    
    whyChoose: {
      title: 'Why Form Your Business in Florida?',
      subtitle: 'Florida connects you to global opportunities',
      benefits: [
        {
          icon: 'DollarSign',
          title: 'No Personal Income Tax',
          description: 'Florida is one of only 9 states with no personal income tax. Keep more of your earnings.'
        },
        {
          icon: 'Globe',
          title: 'Gateway to Latin America',
          description: 'Miami is the business hub for US-Latin America trade. Access 650+ million consumers.'
        },
        {
          icon: 'Users',
          title: 'Growing Population',
          description: 'Florida adds 1,000+ new residents daily. Expanding market and workforce.'
        },
        {
          icon: 'Building2',
          title: 'Diverse Economy',
          description: 'Tourism, tech, finance, healthcare, and real estate create diverse opportunities.'
        }
      ]
    },
    
    taxInfo: {
      title: 'Florida Tax Environment',
      items: [
        { label: 'Personal Income Tax', value: 'None', highlight: true },
        { label: 'Corporate Income Tax', value: '5.5%', highlight: false },
        { label: 'Sales Tax', value: '6% + local', highlight: false },
        { label: 'LLC Annual Report', value: '$138.75', highlight: false }
      ]
    },
    
    faqs: [
      {
        question: 'Why do international entrepreneurs choose Florida?',
        answer: 'Florida offers no income tax, proximity to Latin America, diverse banking options (many banks serve international clients), and a large Spanish-speaking population. Miami has become a hub for international business.'
      },
      {
        question: 'What are Florida\'s ongoing requirements?',
        answer: 'Florida requires an annual report filed by May 1 ($138.75 for LLCs, $150 for corporations). There\'s also a 5.5% corporate income tax on profits over $50,000.'
      }
    ]
  },
  
  'nevada': {
    tagline: 'Privacy and Asset Protection',
    overview: `Nevada offers strong privacy laws, no state income tax, and robust asset protection. While filing fees are higher than some states, Nevada's lack of information sharing agreements with the IRS and strong corporate veil protections attract privacy-conscious business owners.`,
    
    whyChoose: {
      title: 'Why Form Your Business in Nevada?',
      subtitle: 'Nevada prioritizes privacy and protection',
      benefits: [
        {
          icon: 'Shield',
          title: 'Maximum Privacy',
          description: 'Nevada does not share information with the IRS. Officers and directors need not be disclosed.'
        },
        {
          icon: 'DollarSign',
          title: 'No State Income Tax',
          description: 'No corporate income tax, personal income tax, or franchise tax.'
        },
        {
          icon: 'Scale',
          title: 'Strong Asset Protection',
          description: 'Nevada\'s charging order is the exclusive remedy for LLC creditors.'
        }
      ]
    },
    
    taxInfo: {
      title: 'Nevada Tax Environment',
      items: [
        { label: 'Personal Income Tax', value: 'None', highlight: true },
        { label: 'Corporate Income Tax', value: 'None', highlight: true },
        { label: 'Franchise Tax', value: 'None', highlight: true },
        { label: 'Commerce Tax', value: '0.051%-0.331%', highlight: false },
        { label: 'Commerce Tax Threshold', value: '$4M+ revenue', highlight: true }
      ]
    },
    
    faqs: [
      {
        question: 'Why are Nevada filing fees higher?',
        answer: 'Nevada charges $425 for LLC formation ($75 filing + $150 business license + $200 initial list). However, there\'s no income tax or franchise tax, which can offset higher initial costs for profitable businesses.'
      },
      {
        question: 'Is Nevada better than Wyoming for privacy?',
        answer: 'Both offer excellent privacy. Nevada does not share data with the IRS. Wyoming offers lower fees and simpler compliance. For pure privacy and no income tax, both are excellent choices.'
      }
    ]
  },
  
  'california': {
    tagline: 'The Innovation Capital of the World',
    overview: `California is home to Silicon Valley, Hollywood, and the world's fifth-largest economy. While taxes and regulations are higher than other states, California offers unmatched access to talent, venture capital, and major markets. Many businesses must register in California if they have California customers or employees.`,
    
    whyChoose: {
      title: 'Why Form Your Business in California?',
      subtitle: 'Access to the world\'s innovation ecosystem',
      benefits: [
        {
          icon: 'Globe',
          title: 'Massive Market',
          description: '40 million residents and the 5th largest economy in the world.'
        },
        {
          icon: 'DollarSign',
          title: 'Venture Capital Hub',
          description: 'More VC funding flows through California than any other state.'
        },
        {
          icon: 'Users',
          title: 'Top Talent Pool',
          description: 'Access to engineers, designers, and professionals from top universities.'
        },
        {
          icon: 'Building2',
          title: 'Innovation Ecosystem',
          description: 'Silicon Valley, LA tech scene, and startup accelerators provide resources.'
        }
      ]
    },
    
    taxInfo: {
      title: 'California Tax Environment',
      items: [
        { label: 'Personal Income Tax', value: '1%-13.3%', highlight: false },
        { label: 'Franchise Tax (LLC)', value: '$800/year minimum', highlight: false },
        { label: 'Corporate Tax', value: '8.84%', highlight: false },
        { label: 'Sales Tax', value: '7.25% + local', highlight: false }
      ]
    },
    
    faqs: [
      {
        question: 'Should I form my LLC in California or another state?',
        answer: 'If you physically operate in California (office, employees, or significant sales), you\'ll likely need to register in California regardless of where you form. California charges $800/year franchise tax to all LLCs doing business in CA. Many CA businesses form in Wyoming but still pay CA taxes.'
      },
      {
        question: 'What is California\'s $800 franchise tax?',
        answer: 'All LLCs and corporations doing business in California must pay an $800 minimum annual franchise tax, regardless of income. This is in addition to the LLC\'s home state fees. First-year exemption is available for new LLCs.'
      }
    ]
  }
};

// Default content for states without specific content
const DEFAULT_STATE_CONTENT = {
  tagline: 'Form Your Business with Confidence',
  overview: 'Form your LLC or Corporation with professional support and expert guidance through the entire process.',
  whyChoose: {
    title: 'Why Form Your Business Here?',
    subtitle: 'Key advantages of this jurisdiction',
    benefits: [
      { icon: 'Shield', title: 'Liability Protection', description: 'Protect personal assets from business debts and lawsuits.' },
      { icon: 'FileText', title: 'Simple Compliance', description: 'Straightforward annual reporting requirements.' },
      { icon: 'Clock', title: 'Fast Processing', description: 'Quick turnaround on state filings.' },
      { icon: 'DollarSign', title: 'Clear Fee Structure', description: 'Transparent state filing fees.' }
    ]
  },
  faqs: [
    { question: 'How long does formation take?', answer: 'Processing times vary but typically range from 3-10 business days. Expedited options are often available.' },
    { question: 'Do I need to live in this state?', answer: 'No. You can form a business in any state regardless of where you live. You will need a registered agent in the formation state.' },
    { question: 'What are the ongoing requirements?', answer: 'Most states require annual or biennial reports and maintaining a registered agent. Specific requirements vary by state and entity type.' }
  ]
};

const IconMap = {
  DollarSign, Shield, FileText, Scale, Clock, Globe, Building2, Users, MapPin, Check
};

// FAQ Accordion Component
const FAQAccordion = ({ faqs }) => {
  const [openIndex, setOpenIndex] = useState(null);
  
  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div key={index} className="bg-[#111c17] border border-white/10" data-testid={`state-faq-item-${index}`}>
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full px-6 py-5 text-left flex items-center justify-between gap-4"
          >
            <span className="font-medium text-white">{faq.question}</span>
            <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${openIndex === index ? 'rotate-180' : ''}`} />
          </button>
          {openIndex === index && (
            <div className="px-6 pb-5">
              <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const USAStatePage = () => {
  const { state: stateSlug } = useParams();
  const [stateData, setStateData] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [stateRes, faqsRes] = await Promise.all([
          axios.get(`${API}/public/state-pages/${stateSlug}`),
          axios.get(`${API}/public/faq-items`, { params: { category: 'formation', limit: 5 } })
        ]);
        
        setStateData(stateRes.data);
        setFaqs(faqsRes.data.items || []);
      } catch (err) {
        console.error('Error fetching state page:', err);
        setError(err.response?.status === 404 ? 'not_found' : 'error');
      } finally {
        setLoading(false);
      }
    };

    if (stateSlug) fetchData();
  }, [stateSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1310] text-slate-200 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-emerald-500/40 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error === 'not_found' || !stateData) {
    return (
      <div className="min-h-screen bg-[#0b1310] text-slate-200">
        <Header />
        <div className="flex items-center justify-center py-32" data-testid="state-not-found">
          <SEOHead title="State Not Found" siteName={SITE_NAME} noindex={true} />
          <div className="text-center px-4">
            <h1 className="font-serif text-3xl font-semibold text-white mb-4">State Not Found</h1>
            <p className="text-slate-400 mb-8">The state you're looking for doesn't exist or isn't available yet.</p>
            <Link to="/usa/" className="btn-corporate inline-flex items-center gap-2">
              View All States <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const content = STATE_CONTENT[stateSlug] || DEFAULT_STATE_CONTENT;

  const entityTypes = [
    { slug: 'llc', name: 'LLC', fee: stateData.govt_fee_llc, desc: 'Limited Liability Company', popular: true },
    { slug: 'c-corp', name: 'C-Corporation', fee: stateData.govt_fee_ccorp, desc: 'For startups & investors', popular: stateSlug === 'delaware' },
    { slug: 'partnership', name: 'Partnership', fee: stateData.govt_fee_partnership, desc: 'Limited Partnership', popular: false }
  ];

  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@type": "State",
    "name": stateData.state_name,
    "url": `${BASE_URL}/usa/states/${stateSlug}/`
  };

  return (
    <div className="min-h-screen bg-[#0b1310] text-slate-200" data-testid="usa-state-page">
      <SEOHead
        title={`Form a Company in ${stateData.state_name} | LLC & Corporation Formation`}
        description={stateData.meta_description || `Form an LLC or Corporation in ${stateData.state_name}. LLC filing fee: $${stateData.govt_fee_llc}. Fast processing, expert support included.`}
        siteName={SITE_NAME}
        canonical={`${BASE_URL}/usa/states/${stateSlug}/`}
        keywords={`${stateData.state_name} LLC, ${stateData.state_name} corporation, form company in ${stateData.state_name}, ${stateData.state_code} business formation`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLdSchema)}</script>
      </Helmet>

      <Header />

      {/* Hero Section - StartEase Agent Style */}
      <section className="bg-[#1e293b] relative" data-testid="state-hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <nav className="mb-10" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-slate-400">
              <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
              <li><ChevronRight className="w-4 h-4" /></li>
              <li><Link to="/usa/" className="hover:text-white transition-colors">US Formation</Link></li>
              <li><ChevronRight className="w-4 h-4" /></li>
              <li className="text-emerald-400 font-medium">{stateData.state_name}</li>
            </ol>
          </nav>
          
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="inline-flex items-center gap-2 bg-slate-800 text-white px-4 py-2 mb-6 text-sm font-medium">
                <MapPin className="w-4 h-4 text-emerald-400" />
                {stateData.state_code}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl text-white tracking-tight leading-[1.1] mb-4">
                {stateData.state_name} Business Formation
              </h1>
              <p className="text-xl text-emerald-400 font-medium mb-6">{content.tagline}</p>
              <p className="text-lg text-slate-400 leading-relaxed mb-8">
                {content.overview}
              </p>
              <div className="flex flex-wrap gap-8 text-sm text-slate-400 mb-10">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span>LLC from <span className="text-white font-medium">${stateData.govt_fee_llc}</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span>{stateData.processing_time_days} Business Days</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span>Expert Support</span>
                </div>
              </div>
              <a href={APP_URL} className="inline-flex items-center justify-center px-8 py-4 bg-emerald-500 text-[#04110c] font-semibold hover:bg-emerald-400 transition-colors" data-testid="state-hero-cta">
                Start {stateData.state_code} Formation <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </div>
            
            {/* Entity Type Cards */}
            <div className="space-y-4" data-testid="state-entity-cards">
              <h2 className="text-lg font-semibold text-white mb-4">Choose Your Entity Type</h2>
              {entityTypes.map((entity) => (
                <Link
                  key={entity.slug}
                  to={`/usa/states/${stateSlug}/${entity.slug}/`}
                  className="block bg-slate-800 border border-slate-700 p-6 hover:border-emerald-500 hover:bg-slate-700 transition-all group"
                  data-testid={`state-entity-${entity.slug}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-white group-hover:text-emerald-400 transition-colors">
                          {stateData.state_name} {entity.name}
                        </h3>
                        {entity.popular && <span className="text-xs bg-emerald-500 text-white px-2 py-0.5">Popular</span>}
                      </div>
                      <p className="text-slate-400 text-sm">{entity.desc}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-bold text-xl">${entity.fee}</p>
                      <p className="text-slate-400 text-xs">state fee</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      {content.whyChoose && (
        <section className="py-24 md:py-32 bg-[#070d0b]" data-testid="state-why-choose">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl mb-16">
              <p className="text-emerald-400 font-semibold text-sm uppercase tracking-widest mb-4">Advantages</p>
              <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight mb-6">
                {content.whyChoose.title}
              </h2>
              <p className="text-xl text-slate-400">{content.whyChoose.subtitle}</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {content.whyChoose.benefits.map((benefit, index) => {
                const IconComponent = IconMap[benefit.icon] || Check;
                return (
                  <div key={index} className="bg-[#111c17] border border-white/10 p-8">
                    <div className="w-12 h-12 bg-[#111c17] flex items-center justify-center mb-6">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-3">{benefit.title}</h3>
                    <p className="text-slate-400">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* State Advantages from API */}
      {stateData.advantages && stateData.advantages.length > 0 && !content.whyChoose && (
        <section className="py-24 md:py-32 bg-[#070d0b]" data-testid="state-advantages-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl mb-16">
              <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Advantages</p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
                Why {stateData.state_name}?
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stateData.advantages.map((advantage, index) => (
                <div key={index} className="bg-[#111c17] border border-white/10 p-8 flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#111c17] flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-slate-400">{advantage}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Tax Environment */}
      {content.taxInfo && (
        <section className="py-24 md:py-32 bg-[#111c17]" data-testid="state-tax-info">
          <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="text-center mb-16">
              <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Tax Environment</p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight">
                {content.taxInfo.title}
              </h2>
            </div>
            
            <div className="bg-[#070d0b] border border-white/10 overflow-hidden">
              {content.taxInfo.items.map((item, index) => (
                <div key={index} className={`flex items-center justify-between px-6 py-4 ${index !== content.taxInfo.items.length - 1 ? 'border-b border-white/10' : ''}`}>
                  <span className="text-slate-400">{item.label}</span>
                  <span className={`font-semibold ${item.highlight ? 'text-emerald-400' : 'text-white'}`}>
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Filing Fees Comparison */}
      <section className="py-24 md:py-32 bg-[#070d0b]" data-testid="state-fees-comparison">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Filing Fees</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight">
              {stateData.state_name} State Filing Fees
            </h2>
          </div>
          
          <div className="bg-[#111c17] border border-white/10 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#111c17] text-white">
                  <th className="text-left px-6 py-4 font-semibold">Entity Type</th>
                  <th className="text-right px-6 py-4 font-semibold">State Fee</th>
                  <th className="text-right px-6 py-4 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {entityTypes.map((entity, index) => (
                  <tr key={index} className="border-b border-white/10 last:border-b-0">
                    <td className="px-6 py-4">
                      <span className="font-semibold text-white">{entity.name}</span>
                      <p className="text-sm text-slate-400">{entity.desc}</p>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-xl font-semibold text-white">${entity.fee}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link to={`/usa/states/${stateSlug}/${entity.slug}/`} className="text-white hover:text-emerald-400 font-medium text-sm inline-flex items-center gap-1">
                        Start <ArrowRight className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Compliance Requirements */}
      {content.complianceRequirements && (
        <section className="py-24 md:py-32 bg-[#111c17]" data-testid="state-compliance">
          <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="text-center mb-16">
              <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Compliance</p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight">
                {content.complianceRequirements.title}
              </h2>
            </div>
            
            <div className="space-y-4">
              {content.complianceRequirements.items.map((item, index) => (
                <div key={index} className="bg-[#070d0b] border border-white/10 p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-white">{item.requirement}</h3>
                      <p className="text-slate-400 text-sm">{item.notes}</p>
                    </div>
                    <div className="flex gap-6 text-sm">
                      <div>
                        <p className="text-slate-400">Deadline</p>
                        <p className="font-medium text-white">{item.deadline}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Fee</p>
                        <p className="font-medium text-white">{item.fee}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Section */}
      <FeesSection entityType="llc" stateData={stateData} stateName={stateData.state_name} />

      {/* Filings Sections */}
      <FilingsSection entityType="llc" stateName={stateData.state_name} />

      {/* FAQs Section */}
      <section className="py-24 md:py-32 bg-[#070d0b]" data-testid="state-faqs-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">FAQs</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-slate-400">Common questions about {stateData.state_name} business formation</p>
          </div>
          
          <FAQAccordion faqs={content.faqs || DEFAULT_STATE_CONTENT.faqs} />
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1e293b]" data-testid="state-cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 text-center">
          <h2 className="text-3xl md:text-4xl text-white tracking-tight mb-6">
            Ready to Form Your {stateData.state_name} Company?
          </h2>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Join thousands of entrepreneurs who've formed their {stateData.state_name} business with {SITE_NAME}.
            LLC from ${stateData.govt_fee_llc}. {stateData.processing_time_days} day timeline.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={APP_URL}
              className="inline-flex items-center justify-center px-8 py-4 bg-emerald-500 text-[#04110c] font-semibold hover:bg-emerald-400 transition-colors"
              data-testid="state-footer-cta-primary"
            >
              Start {stateData.state_code} Formation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
            <a
              href="mailto:hi@starteaseai.com"
              className="inline-flex items-center justify-center px-8 py-4 bg-slate-700 text-white font-medium hover:bg-slate-600 transition-colors"
            >
              Talk to an Expert
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default USAStatePage;
